import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { timetablesApi, classesApi, teachersApi, schoolYearsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, PencilSimple, Trash, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAY_KEYS = ['dayMonday', 'dayTuesday', 'dayWednesday', 'dayThursday', 'dayFriday', 'daySaturday'];

const Timetables = () => {
  const { t, language } = useLanguage();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeYear, setActiveYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ day_of_week: '0', start_time: '08:00', end_time: '09:00', subject: '', teacher_id: '', room: '' });

  useEffect(() => {
    Promise.all([classesApi.getAll(), teachersApi.getAll(), schoolYearsApi.getActive()])
      .then(([cRes, tRes, yRes]) => {
        setClasses(cRes.data || []);
        setTeachers(tRes.data || []);
        setActiveYear(yRes.data?.name || '');
        if ((cRes.data || []).length) setSelectedClass(cRes.data[0].name);
      })
      .catch(() => toast.error(t('error')));
  }, []);

  useEffect(() => {
    if (selectedClass) loadSlots();
  }, [selectedClass]);

  const loadSlots = async () => {
    try {
      setLoading(true);
      const { data } = await timetablesApi.getAll({ class_name: selectedClass });
      setSlots(data || []);
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const openNew = (day) => {
    setEditing(null);
    setForm({ day_of_week: String(day ?? 0), start_time: '08:00', end_time: '09:00', subject: '', teacher_id: '', room: '' });
    setDialogOpen(true);
  };

  const openEdit = (slot) => {
    setEditing(slot);
    setForm({
      day_of_week: String(slot.day_of_week),
      start_time: slot.start_time,
      end_time: slot.end_time,
      subject: slot.subject || '',
      teacher_id: slot.teacher_id || '',
      room: slot.room || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        class_name: selectedClass,
        school_year: activeYear,
        day_of_week: Number(form.day_of_week),
        start_time: form.start_time,
        end_time: form.end_time,
        subject: form.subject,
        teacher_id: form.teacher_id || null,
        teacher_name: '',
        room: form.room
      };
      if (editing) {
        await timetablesApi.update(editing.id, payload);
      } else {
        await timetablesApi.create(payload);
      }
      toast.success(t('success'));
      await loadSlots();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await timetablesApi.delete(id);
      toast.success(t('success'));
      await loadSlots();
    } catch {
      toast.error(t('error'));
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedClass) return;
    try {
      const { data } = await timetablesApi.exportPdf(selectedClass, activeYear);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `emploi_du_temps_${selectedClass}_${activeYear || ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('error'));
    }
  };

  const slotsByDay = useMemo(() => {
    const out = Array.from({ length: 6 }, () => []);
    slots.forEach((s) => {
      if (s.day_of_week >= 0 && s.day_of_week < 6) out[s.day_of_week].push(s);
    });
    out.forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return out;
  }, [slots]);

  return (
    <div className="space-y-6" data-testid="timetables-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar size={28} className="text-[#0052FF]" weight="duotone" />
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('timetables')}</h1>
        </div>
        <div className="flex gap-3 items-center">
          <Label className="font-manrope">{t('class')}:</Label>
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-40" data-testid="timetable-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {classes.map(c => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleDownloadPdf} disabled={!selectedClass} data-testid="export-timetable-pdf">
            <DownloadSimple size={16} className="mr-1" />PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DAY_KEYS.map((key, idx) => (
            <Card key={key} className="border border-[#E2E8F0] shadow-none" data-testid={`day-card-${idx}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-outfit text-base">{t(key)}</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => openNew(idx)} className="text-[#0052FF]" data-testid={`add-slot-day-${idx}`}>
                  <Plus size={16} />
                </Button>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {slotsByDay[idx].length === 0 ? (
                  <p className="text-sm text-[#64748B] py-4 text-center">{t('noData')}</p>
                ) : (
                  slotsByDay[idx].map((s) => (
                    <div key={s.id} className="p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] hover:bg-white transition-colors" data-testid={`slot-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-[#0052FF]">{s.start_time} - {s.end_time}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)}><PencilSimple size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(s.id)}><Trash size={14} /></Button>
                        </div>
                      </div>
                      <p className="font-outfit font-semibold text-sm text-[#0F172A]">{s.subject}</p>
                      {s.teacher_name && <p className="text-xs text-[#64748B]">{s.teacher_name}</p>}
                      {s.room && <p className="text-xs text-[#64748B]">{language === 'fr' ? 'Salle' : 'Room'}: {s.room}</p>}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Slot dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">{editing ? t('edit') : t('addSlot')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Jour' : 'Day'}</Label>
                <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                  <SelectTrigger data-testid="slot-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAY_KEYS.map((k, i) => <SelectItem key={k} value={String(i)}>{t(k)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('subject')}</Label>
                <Input data-testid="slot-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('startTime')}</Label>
                <Input type="time" data-testid="slot-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>{t('endTime')}</Label>
                <Input type="time" data-testid="slot-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('teacher')}</Label>
              <Select value={form.teacher_id || 'none'} onValueChange={(v) => setForm({ ...form, teacher_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="slot-teacher"><SelectValue placeholder={t('selectTeacher')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {teachers.map(tt => <SelectItem key={tt.id} value={tt.id}>{tt.first_name} {tt.last_name} {tt.subject ? `(${tt.subject})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Salle' : 'Room'}</Label>
              <Input data-testid="slot-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" data-testid="save-slot-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Timetables;
